// Fatigue Management dashboard.
//
// Rebuilt on shadcn/ui + scoped Tailwind, matching the ROI Reporting rebuild:
// the page owns a .fatigue-page scope so Tailwind utilities apply here without
// Preflight touching the MUI pages around it, and `dark` is driven from the MUI
// palette mode so the two systems agree about theme.

import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Button as MuiButton } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useClientScope } from '../../../../../shared/contexts';
import { FilterDrawer, getAppliedFilterCount, ComparisonToggle } from '../../../../../shared/components';
import { usePersistedFilters, useComparisonMode, useComparisonGroups } from '../../../../../shared/hooks';
import { useFatigueSummary } from '../data/useFatigueData';
import { NOT_MEDICAL } from '../data/methodology';
import { NoteLine } from '../components/primitives';
import { OverviewSection } from '../sections/OverviewSection';
import { ActionsSection, CohortRiskSection, MethodologySection, OperationsSection } from '../sections/sections';
import '../../../../../styles/fatigue-shadcn.css';

/** FM has no date-range UI of its own (FatigueWindow is server-chosen by default); fixed null range for the shared filter/compare hooks. */
const NO_DATE_RANGE: [null, null] = [null, null];

type SectionKey = 'overview' | 'operations' | 'cohorts' | 'actions' | 'methodology';

const SECTIONS: { key: SectionKey; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'operations', label: 'Operations safety' },
  { key: 'cohorts', label: 'Cohort risk' },
  { key: 'actions', label: 'Wellbeing actions' },
  { key: 'methodology', label: 'Methodology' },
];

export const FatigueManagement: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { effectiveClientId, isCentralAdmin } = useClientScope();

  const [section, setSection] = useState<SectionKey>('overview');
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // PEAK-263: same shared filter model (and the same persisted storage key)
  // as every other admin dashboard, instead of FM's own Site/Shift/Data-
  // quality bar — so a filter chosen elsewhere in the admin suite carries
  // over here too, and FM gets the PEAK-266 cross-client reset for free.
  const { filters, setFilters } = usePersistedFilters();
  const activeFiltersCount = getAppliedFilterCount(filters);

  // Compare mode: one filter dimension, 2+ selected values, rendered as
  // side-by-side columns — same mechanism as every other dashboard. Shipped
  // for the Overview tab only this pass; Operations/Cohort risk already show
  // a full per-cohort breakdown table, so "compare" there needs its own
  // design rather than reusing the card-column pattern.
  const comparison = useComparisonMode(filters);
  const { groups, overflowCount } = useComparisonGroups(
    filters,
    NO_DATE_RANGE,
    effectiveClientId,
    comparison,
    isCentralAdmin,
  );

  // The same query every section reads — cached, so this costs no extra request.
  const { data } = useFatigueSummary(filters);

  const handleApplyFilters = (next: typeof filters) => setFilters(next);

  return (
    <div className={cn('fatigue-page flex flex-col gap-6 p-1', isDark && 'dark')}>
      {/* Title and section nav share a row, as on the ROI page, so the nav
          doesn't eat vertical space on smaller screens (FM-13). */}
      <div className='flex flex-wrap items-start justify-between gap-4'>
        <div className='min-w-0'>
          <h1 className='text-xl font-semibold'>Fatigue Management</h1>
          <p className='mt-1 text-sm text-muted-foreground'>
            Cohort-level fatigue exposure, load alignment and scheduling risk across the workforce.
          </p>
        </div>
        <nav aria-label='Dashboard sections' className='flex flex-wrap gap-2'>
          {SECTIONS.map((item) => (
            <Button
              key={item.key}
              variant={section === item.key ? 'default' : 'outline'}
              size='sm'
              aria-current={section === item.key ? 'page' : undefined}
              onClick={() => setSection(item.key)}
            >
              {item.label}
            </Button>
          ))}
        </nav>
      </div>

      {/* FM-15 / PEAK-263 — one shared filter bar for every section, plus Compare on Overview. */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
        <MuiButton variant='outlined' size='small' startIcon={<FilterListIcon />} onClick={() => setIsFilterOpen(true)}>
          {activeFiltersCount > 0 ? `Filter (${activeFiltersCount})` : 'Filter'}
        </MuiButton>
        <ComparisonToggle comparison={comparison} />
      </Box>

      {data?.caveats.suppressedCohorts && data.caveats.suppressedCohorts.length > 0 && (
        <NoteLine>
          Fewer than {data.caveats.minCohortSize} people with data in {data.caveats.suppressedCohorts.join(', ')}, so
          those teams show no figures of their own — their people still count towards the headline numbers.
        </NoteLine>
      )}

      {section === 'overview' && (
        <OverviewSection filters={filters} comparison={comparison} groups={groups} overflowCount={overflowCount} />
      )}
      {section === 'operations' && <OperationsSection filters={filters} />}
      {section === 'cohorts' && <CohortRiskSection filters={filters} />}
      {section === 'actions' && <ActionsSection />}
      {section === 'methodology' && <MethodologySection />}

      <NoteLine>{NOT_MEDICAL}</NoteLine>

      <FilterDrawer
        open={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApply={handleApplyFilters}
        initialFilters={filters}
      />
    </div>
  );
};
